import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
    FaPhoneAlt,
    FaEnvelope,
    FaMapMarkerAlt,
    FaHistory,
    FaEdit,
    FaTrash,
    FaUser,
    FaWeight,
    FaRuler,
    FaTint,
    FaBirthdayCake,
    FaVenusMars,
    FaPhone,
    FaExclamationTriangle,
    FaSortUp,
    FaKey,
} from "react-icons/fa";
import { MdSave, MdCancel } from "react-icons/md";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { IoIosHourglass } from "react-icons/io";
import { apiGetFullProfile, apiUpdateProfile, getAllPredictions, apiGetReviewRequests, apiGetReviewRequest, apiDeletePrediction } from "../api/api";
import Header from '../Nav_Bar/Header';
import MaleAvatar from "../Assets/male-avatar.png";
import FemaleAvatar from "../Assets/female-avatar.png";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import UpdateProfilePopup from '../components/UpdateProfilePopup';
import PatientReviewModal from '../components/PatientReviewModal';
import ImageCropModal from '../components/ImageCropModal';
import ChangePassword from '../components/ChangePassword';
import DropDown from "../components/ui/DropDown";

const CardSection = ({ title, icon, children, editHandler, gradient = false }) => (
    <div className={`${gradient ? 'bg-gradient-to-br from-white via-gray-50 to-white' : 'bg-white'} border border-gray-200 rounded-xl shadow-lg p-4`}>
        <div className="flex justify-between items-center mb-3 pb-2 border-b border-gray-100">
            <h3 className="text-base font-bold text-gray-800 flex items-center gap-2">
                <span className="p-1.5 bg-blue-50 rounded-lg">{icon && icon}</span>
                {title}
            </h3>
            {editHandler && (
                <button
                    onClick={editHandler}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 hover:text-white hover:bg-blue-600 border border-blue-600 rounded-lg transition-all duration-200 transform hover:scale-105"
                >
                    <FaEdit className="text-xs" />
                    Edit
                </button>
            )}
        </div>
        {children}
    </div>
);

const InfoItem = ({ icon, label, value, iconColor = "text-gray-400" }) => (
    <div className="flex items-start gap-2 p-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-200">
        <div className={`${iconColor} mt-0.5 text-base`}>{icon}</div>
        <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
            <p className="text-sm font-semibold text-gray-800 break-words">{value || "N/A"}</p>
        </div>
    </div>
);

const UserProfile = () => {
    const location = useLocation();
    const [patient, setPatient] = useState(null);
    const [loading, setLoading] = useState(true);
    const [predictionsLoading, setPredictionsLoading] = useState(true);
    const [reviewRequestsLoading, setReviewRequestsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editMode, setEditMode] = useState(null);
    const [editData, setEditData] = useState({});
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [showUpdatePopup, setShowUpdatePopup] = useState(false);
    const [predictions, setPredictions] = useState([]);
    const [reviewRequests, setReviewRequests] = useState([]);
    const [showPatientReview, setShowPatientReview] = useState(false);
    const [patientReviewData, setPatientReviewData] = useState(null);
    const [reviewLoading, setReviewLoading] = useState(false);
    const [reviewError, setReviewError] = useState(null);
    const [sortBy, setSortBy] = useState('date');
    const [sortOrder, setSortOrder] = useState('desc');
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [showCropModal, setShowCropModal] = useState(false);
    const [imageSrc, setImageSrc] = useState(null);
    const [removingImage, setRemovingImage] = useState(false);

    useEffect(() => {
        fetchProfile();
        fetchPredictions();
        fetchReviewRequests();
    }, []);

    useEffect(() => {
        if (patient) {
            const popupShown = sessionStorage.getItem('profilePopupShown');
            console.log("Checking Profile Completeness:", patient);
            const isIncomplete = !patient.name || !patient.age || !patient.phone || !patient.gender || !patient.address || !patient.height || !patient.weight || !patient.bloodGroup;
            console.log("Is profile incomplete?", isIncomplete);
            console.log(`Is Incomplete: ${isIncomplete}, Popup Shown Previously: ${popupShown}`);
            if (isIncomplete && !popupShown) {
                console.log("TRIGGERING POPUP");
                setShowUpdatePopup(true);
                sessionStorage.setItem('profilePopupShown', 'true');
            }
            // Auto-open basic info edit mode if navigated from update popup
            if (location.state?.autoEditBasic) {
                setEditMode('Basic Information');
                setEditData({ ...patient });
                // Clear the state to prevent re-triggering on page refresh
                window.history.replaceState({}, document.title);
            }
        }
    }, [patient, location.state]);

    const fetchPredictions = async () => {
        try {
            setPredictionsLoading(true);
            const response = await getAllPredictions();
            setPredictions(response.data || []);
        } catch (error) {
            console.error("Failed to fetch predictions:", error);
        } finally {
            setPredictionsLoading(false);
        }
    };

    const fetchReviewRequests = async () => {
        try {
            setReviewRequestsLoading(true);
            const response = await apiGetReviewRequests();
            setReviewRequests(response.data?.requests || []);
        } catch (error) {
            console.error("Failed to fetch review requests:", error);
        } finally {
            setReviewRequestsLoading(false);
        }
    };

    const getReviewStatus = (predictionId) => {
        const request = reviewRequests.find(
            (req) => req.predictionId === predictionId
        );
        if (!request) return null;
        return request.status;
    };

    const renderStatusBadge = (status) => {
        if (!status) {
            return (
                <span className="px-2.5 py-1 text-xs font-bold rounded-full bg-gray-200 text-gray-600">
                    No Request
                </span>
            );
        }

        const statusConfig = {
            pending: {
                bg: 'bg-yellow-100',
                text: 'text-yellow-700',
                icon: <IoIosHourglass className="text-yellow-700 h-3.5 w-3.5" />,
                label: 'Pending'
            },
            reviewed: {
                bg: 'bg-green-100',
                text: 'text-green-700',
                icon: '✓',
                label: 'Approved'
            },
            rejected: {
                bg: 'bg-red-100',
                text: 'text-red-700',
                icon: '✗',
                label: 'Rejected'
            }
        };

        const config = statusConfig[status] || statusConfig.pending;

        return (
            <span className={`px-2.5 py-1 text-xs font-semibold rounded-full inline-flex items-center gap-1 ${config.bg} ${config.text}`}>
                <span>{config.icon}</span>
                {config.label}
            </span>
        );
    };

    const getSortedPredictions = () => {
        if (!predictions || predictions.length === 0) return [];

        let sorted = [...predictions];

        sorted.sort((a, b) => {
            let comparison = 0;

            if (sortBy === 'disease') {
                const nameA = (a.result?.predicted_label || '').toLowerCase();
                const nameB = (b.result?.predicted_label || '').toLowerCase();
                comparison = nameA.localeCompare(nameB);
            } else if (sortBy === 'date') {
                const dateA = new Date(a.createdAt);
                const dateB = new Date(b.createdAt);
                comparison = dateA - dateB;
            } else if (sortBy === 'status') {
                const statusA = getReviewStatus(a.id) || 'none';
                const statusB = getReviewStatus(b.id) || 'none';
                const statusOrder = { 'reviewd': 1, 'pending': 2, 'rejected': 3, 'none': 4 };
                comparison = (statusOrder[statusA] || 5) - (statusOrder[statusB] || 5);
            }

            return sortOrder === 'asc' ? comparison : -comparison;
        });

        return sorted;
    };

    const fetchProfile = async () => {
        try {
            setLoading(true);
            const response = await apiGetFullProfile();
            setPatient(response.data);
            setError(null);
        } catch (error) {
            console.error("Failed to fetch profile:", error);
            const errorMsg = error.response?.data?.detail?.error || error.response?.data?.detail || error.message || "Failed to load profile";
            setError(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const handleImageSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                alert('Please select an image file');
                return;
            }
            if (file.size > 5 * 1024 * 1024) {
                alert('Image size should be less than 5MB');
                return;
            }
            const reader = new FileReader();
            reader.onload = () => {
                setImageSrc(reader.result);
                setShowCropModal(true);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleCropSave = (croppedImage) => {
        setImagePreview(croppedImage);
        setSelectedImage(croppedImage);
        setImageSrc(null);
    };

    const handleImageUpload = async () => {
        if (!selectedImage) return;
        setLoading(true);
        try {
            const response = await apiUpdateProfile({
                profileImage: selectedImage
            });
            setPatient(response.data.user);
            setSelectedImage(null);
            setImagePreview(null);
            toast.success('Profile picture updated successfully');
        } catch (error) {
            console.error('Failed to upload image:', error);
            alert(error.response?.data?.detail || 'Failed to upload image');
        } finally {
            setLoading(false);
        }
    };

    const handleCancelImage = () => {
        setSelectedImage(null);
        setImagePreview(null);
    };

    const handleRemoveImage = async () => {
        if (!window.confirm('Are you sure you want to remove your profile picture?')) {
            return;
        }
        setRemovingImage(true);
        try {
            setLoading(true);
            const response = await apiUpdateProfile({
                profileImage: ""
            });
            setPatient(response.data.user);
            setSelectedImage(null);
            setImagePreview(null);
            toast.success('Profile picture removed successfully');
        } catch (error) {
            console.error('Failed to remove image:', error);
            alert(error.response?.data?.detail || 'Failed to remove image');
        } finally {
            setLoading(false);
            setTimeout(() => setRemovingImage(false), 300); // Reset animation state
        }
    };

    const DefaultAvatar = ({ gender, className }) => {
        const getDefaultAvatar = () => {
            if (gender === "Female") {
                return FemaleAvatar;
            } else if (gender === "Male") {
                return MaleAvatar;
            } else {
                return MaleAvatar;
            }
        };
        return (
            <img
                src={getDefaultAvatar()}
                alt="Default Avatar"
                className={className}
            />
        );
    };

    const handleEdit = (section) => {
        setEditMode(section);
        setEditData({ ...patient });
    };

    const validateBasicInfo = () => {
        if (editData.age && (editData.age < 0 || editData.age > 150)) {
            alert("Please enter a valid age");
            return false;
        }
        if (editData.phone && !/^\+?[\d\s-]{10,}$/.test(editData.phone)) {
            alert("Please enter a valid phone number");
            return false;
        }
        return true;
    };

    const handleSave = async (section) => {
        if (section === "Basic Information" && !validateBasicInfo()) {
            return;
        }
        try {
            setLoading(true);
            const response = await apiUpdateProfile(editData);
            setPatient(response.data.user);
            setEditMode(null);
            setEditData({});
            toast.success(response.data.message);
        } catch (error) {
            console.error("Failed to update profile:", error);
            alert(error.response?.data?.detail || "Failed to update profile");
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setEditMode(null);
        setEditData({});
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setEditData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        // Add 5 hours to compensate for timezone difference (UTC+5)
        date.setHours(date.getHours() + 5);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        let hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12 || 12;
        hours = String(hours).padStart(2, '0');
        return `${day}/${month}/${year} ${hours}:${minutes}:${seconds} ${ampm}`;
    };

    const handleDeleteAnalysis = async (predictionId) => {
        if (!window.confirm('Are you sure you want to delete this analysis? This action cannot be undone.')) {
            return;
        }
        try {
            await apiDeletePrediction(predictionId);
            toast.success('Analysis deleted successfully');

            setPredictions(prev => prev.filter(p => p.id !== predictionId));

            setReviewRequests(prev => prev.filter(r => r.predictionId !== predictionId));
        } catch (error) {
            console.error("Failed to delete analysis:", error);
            const errorMsg = error.response?.data?.detail?.error || error.response?.data?.detail || 'Failed to delete analysis';
            toast.error(errorMsg);
        }
    };

    const handleViewAnalysis = async (predictionId) => {
        try {
            setReviewLoading(true);
            setReviewError(null);
            setShowPatientReview(true);
            const prediction = predictions.find(p => p.id === predictionId);
            if (!prediction) {
                setReviewError("Prediction not found");
                setReviewLoading(false);
                return;
            }
            const reviewRequest = reviewRequests.find(r => r.predictionId === predictionId && (r.status === 'reviewed' || r.status === 'rejected'));
            if (reviewRequest) {
                try {
                    const reviewResponse = await apiGetReviewRequest(reviewRequest.id);
                    const reviewData = reviewResponse.data;
                    if (reviewData.prediction && !reviewData.prediction.imageUrl && prediction.imageUrl) {
                        reviewData.prediction.imageUrl = prediction.imageUrl;
                    }
                    setPatientReviewData(reviewData);
                } catch (err) {
                    console.error("Failed to fetch review details:", err);
                    setPatientReviewData({
                        prediction: prediction,
                        status: 'pending',
                        comment: null,
                        dermatologistInfo: null,
                        reviewedAt: null
                    });
                }
            } else {
                setPatientReviewData({
                    prediction: prediction,
                    status: 'pending',
                    comment: null,
                    dermatologistInfo: null,
                    reviewedAt: null
                });
            }
        } catch (error) {
            console.error("Failed to load analysis:", error);
            setReviewError("Failed to load analysis details");
        } finally {
            setReviewLoading(false);
        }
    };

    if (loading && !patient) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-gray-100">
                <Header />
                <div className="flex items-center justify-center py-20">
                    <div className="text-center">
                        <div className="relative w-16 h-16 mx-auto mb-4">
                            <div className="absolute inset-0 border-4 border-blue-200 rounded-full animate-ping"></div>
                            <div className="relative w-16 h-16 border-4 border-t-blue-600 border-r-blue-600 border-b-transparent border-l-transparent rounded-full animate-spin"></div>
                        </div>
                        <p className="text-base font-semibold text-gray-700 animate-pulse">Loading your profile...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !patient) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 via-red-50 to-gray-100">
                <Header />
                <div className="flex items-center justify-center py-20">
                    <div className="text-center bg-white p-6 rounded-2xl shadow-xl max-w-md">
                        <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                            <FaExclamationTriangle className="text-2xl text-red-600" />
                        </div>
                        <p className="text-red-600 mb-4 font-medium text-sm">{error || "No profile data available"}</p>
                        <button
                            onClick={fetchProfile}
                            className="px-5 py-2 bg-gray-600 text-white text-sm font-semibold rounded-lg hover:bg-gray-700 transform hover:scale-105 transition-all duration-200 shadow-lg"
                        >
                            Try Again
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-gray-100">
            <Header />
            <ToastContainer position="top-right" autoClose={3000} />
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-2">
                <div className="mb-4 mt-2 text-center">
                    <h1 className="text-2xl md:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-blue-800 mb-1">
                        Profile
                    </h1>
                    <p className="text-gray-600 text-xs">Manage your health information and medical records</p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                    {/* Left Column (Profile Info) */}
                    <div className="space-y-5">
                        <div className="bg-gradient-to-br from-white via-blue-50 to-white border border-gray-200 rounded-xl shadow-xl p-5 text-center ">
                            <div className="relative inline-block mb-3">
                                <div className="absolute inset-0 rounded-full blur-xl opacity-30 animate-pulse"></div>
                                <div className={`relative transition-all duration-300 ease-in-out ${removingImage ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}>
                                    {imagePreview || patient.profileImage ? (
                                        <img
                                            src={imagePreview || patient.profileImage}
                                            alt="Profile"
                                            className="w-28 h-28 sm:w-32 sm:h-32 rounded-full object-cover border-4 border-white shadow-2xl"
                                        />
                                    ) : (
                                        <DefaultAvatar
                                            gender={patient.gender}
                                            className="w-28 h-28 sm:w-32 sm:h-32 rounded-full object-cover border-4 border-white shadow-2xl"
                                        />
                                    )}
                                </div>
                                <label
                                    htmlFor="profile-image-input"
                                    className="absolute bottom-1 right-1 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-full p-2 cursor-pointer shadow-lg transition-all transform hover:scale-110"
                                    title="Change profile picture"
                                >
                                    <FaEdit className="h-3.5 w-3.5" />
                                </label>
                                {!selectedImage && patient.profileImage && (
                                    <button
                                        onClick={handleRemoveImage}
                                        disabled={loading}
                                        className="absolute bottom-1 left-1 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-full p-2 cursor-pointer shadow-lg transition-all transform hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed"
                                        title="Remove profile picture"
                                    >
                                        {loading ? <AiOutlineLoading3Quarters className="animate-spin h-3.5 w-3.5" /> : <FaTrash className="h-3.5 w-3.5" />}
                                    </button>
                                )}
                                <input
                                    id="profile-image-input"
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageSelect}
                                    className="hidden"
                                />
                            </div>

                            {selectedImage && (
                                <div className="flex gap-2 mb-3">
                                    <button
                                        onClick={handleImageUpload}
                                        disabled={loading}
                                        className="flex-1 flex items-center justify-center gap-1.5 bg-blue-600 text-white py-2 px-3 rounded-lg hover:bg-blue-700 font-medium text-xs disabled:opacity-50 transition-all transform hover:scale-105 shadow-md"
                                    >
                                        {loading ? <AiOutlineLoading3Quarters className="animate-spin text-sm" /> : <MdSave className="text-sm" />} {loading ? 'Uploading...' : 'Upload'}
                                    </button>
                                    <button
                                        onClick={handleCancelImage}
                                        className="flex-1 flex items-center justify-center gap-1.5 bg-gray-300 text-gray-700 py-2 px-3 rounded-lg hover:bg-gray-400 font-medium text-xs transition-all transform hover:scale-105 shadow-md"
                                    >
                                        <MdCancel className="text-sm" /> Cancel
                                    </button>
                                </div>
                            )}

                            <h2 className="text-xl font-bold text-gray-800 mb-1">
                                {patient.name || patient.username}
                            </h2>
                            <p className="text-blue-600 font-medium text-sm mb-1">{patient.email}</p>
                            <div className="flex items-center justify-center gap-1.5 text-gray-500 text-xs">
                                <FaMapMarkerAlt />
                                <span>{patient.address || "No address provided"}</span>
                            </div>

                            {/* Change Password Button */}
                            <div className="mt-4 flex justify-center gap-3">
                                <button
                                    onClick={() => setShowPasswordModal(true)}
                                    className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white text-xs font-semibold rounded-lg hover:bg-gray-700 transition-all duration-200"
                                >
                                    <FaKey className="text-sm" />
                                    Change Password
                                </button>
                            </div>
                        </div>

                        <CardSection
                            title="Basic Information"
                            icon={<FaUser className="text-blue-600 text-sm" />}
                            editHandler={() => handleEdit("Basic Information")}
                            gradient={true}
                        >
                            {editMode === "Basic Information" ? (
                                <div className="space-y-3">
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">Full Name</label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={editData.name}
                                            onChange={handleInputChange}
                                            placeholder="Enter your full name"
                                            className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">Gender</label>
                                        <DropDown
                                            name="gender"
                                            label="Gender"
                                            value={editData.gender}
                                            onChange={handleInputChange}
                                            options={[
                                                { value: "", label: "Select Gender" },
                                                { value: "Male", label: "Male" },
                                                { value: "Female", label: "Female" }
                                            ]}
                                            borderClass="border-blue-600"
                                            selectedClass="bg-blue-600 text-gray-950"
                                            highlightClass="bg-blue-200 text-gray-900"
                                            ringClass="ring-2 ring-blue-500"
                                            placeholderClass="text-gray-400"
                                        />

                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">Age</label>
                                        <input
                                            type="number"
                                            name="age"
                                            min="0"
                                            max="150"
                                            value={editData.age}
                                            onChange={handleInputChange}
                                            placeholder="Enter age"
                                            className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">Height</label>
                                        <input
                                            type="number"
                                            name="height"
                                            value={editData.height}
                                            onChange={handleInputChange}
                                            placeholder="e.g., 175 cm"
                                            className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">Weight</label>
                                        <input
                                            type="number"
                                            name="weight"
                                            value={editData.weight}
                                            onChange={handleInputChange}
                                            placeholder="e.g., 70 kg"
                                            className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Blood Group</label>
                                        <DropDown
                                            name="bloodGroup"
                                            label="Blood Group"
                                            value={editData.bloodGroup}
                                            onChange={handleInputChange}
                                            options={[
                                                { value: "", label: "Select Group" },
                                                { value: "A+", label: "A+" },
                                                { value: "A-", label: "A-" },
                                                { value: "B+", label: "B+" },
                                                { value: "B-", label: "B-" },
                                                { value: "AB+", label: "AB+" },
                                                { value: "AB-", label: "AB-" },
                                                { value: "O+", label: "O+" },
                                                { value: "O-", label: "O-" },
                                            ]}
                                            borderClass="border-blue-600"
                                            selectedClass="bg-blue-600 text-gray-950"
                                            highlightClass="bg-blue-200 text-gray-900"
                                            ringClass="ring-2 ring-blue-500"
                                            placeholderClass="text-gray-400"
                                        />
                                    </div>
                                    <div className="flex gap-2 pt-1">
                                        <button
                                            onClick={() => handleSave("Basic Information")}
                                            className="flex-1 flex items-center justify-center gap-1.5 bg-blue-600 text-white py-2 text-xs rounded-lg hover:bg-blue-700 font-semibold transition-all transform hover:scale-105 shadow-md"
                                            disabled={loading}
                                        >
                                            {loading ? <AiOutlineLoading3Quarters className="animate-spin text-sm" /> : <MdSave />} {loading ? 'Saving...' : 'Save'}
                                        </button>
                                        <button
                                            onClick={handleCancel}
                                            className="flex-1 flex items-center justify-center gap-1.5 bg-gray-300 text-gray-700 py-2 text-xs rounded-lg hover:bg-gray-400 font-semibold transition-all transform hover:scale-105 shadow-md"
                                        >
                                            <MdCancel /> Cancel
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    <InfoItem icon={<FaVenusMars />} label="Gender" value={patient.gender} iconColor="text-purple-500" />
                                    <InfoItem icon={<FaBirthdayCake />} label="Age" value={patient.age} iconColor="text-pink-500" />
                                    <InfoItem icon={<FaRuler />} label="Height" value={patient.height} iconColor="text-green-500" />
                                    <InfoItem icon={<FaWeight />} label="Weight" value={patient.weight} iconColor="text-orange-500" />
                                    <InfoItem icon={<FaTint />} label="Blood Group" value={patient.bloodGroup} iconColor="text-red-500" />
                                </div>
                            )}
                        </CardSection>

                        <CardSection
                            title="Contact Information"
                            icon={<FaPhone className="text-green-600 text-sm" />}
                            editHandler={() => handleEdit("Contact Information")}
                            gradient={true}
                        >
                            {editMode === "Contact Information" ? (
                                <div className="space-y-3">
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">Phone Number</label>
                                        <input
                                            type="text"
                                            name="phone"
                                            value={editData.phone}
                                            onChange={handleInputChange}
                                            placeholder="+92 312 4567890"
                                            className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">Emergency Contact (Optional)</label>
                                        <input
                                            type="text"
                                            name="emergencyContact"
                                            value={editData.emergencyContact}
                                            onChange={handleInputChange}
                                            placeholder="+92 334 9876543"
                                            className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 mb-1.5">Address</label>
                                        <textarea
                                            name="address"
                                            value={editData.address}
                                            onChange={handleInputChange}
                                            placeholder="Enter your complete address"
                                            rows="2"
                                            className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all resize-none"
                                        />
                                    </div>
                                    <div className="flex gap-2 pt-1">
                                        <button
                                            onClick={() => handleSave("Contact Information")}
                                            className="flex-1 flex items-center justify-center gap-1.5 bg-blue-600 text-white py-2 text-xs rounded-lg hover:bg-blue-700 font-semibold transition-all transform hover:scale-105 shadow-md"
                                            disabled={loading}
                                        >
                                            {loading ? <AiOutlineLoading3Quarters className="animate-spin text-sm" /> : <MdSave />} {loading ? 'Saving...' : 'Save'}
                                        </button>
                                        <button
                                            onClick={handleCancel}
                                            className="flex-1 flex items-center justify-center gap-1.5 bg-gray-300 text-gray-700 py-2 text-xs rounded-lg hover:bg-gray-400 font-semibold transition-all transform hover:scale-105 shadow-md"
                                        >
                                            <MdCancel /> Cancel
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    <InfoItem icon={<FaPhoneAlt />} label="Phone" value={patient.phone} iconColor="text-green-500" />
                                    <InfoItem icon={<FaExclamationTriangle />} label="Emergency Contact (Optional)" value={patient.emergencyContact} iconColor="text-red-500" />
                                    <InfoItem icon={<FaMapMarkerAlt />} label="Address" value={patient.address} iconColor="text-blue-500" />
                                    <InfoItem icon={<FaEnvelope />} label="Email" value={patient.email} iconColor="text-purple-500" />
                                </div>
                            )}
                        </CardSection>
                    </div>

                    <div className="lg:col-span-2 space-y-5">
                        {/* Custom Card Section with Dropdown Filter */}
                        <div className="bg-gradient-to-br from-white via-green-50 to-white border border-gray-200 rounded-xl shadow-lg p-5 backdrop-blur-sm h-full">
                            {/* Custom Header with Title and Filter Dropdown */}
                            <div className="flex items-center justify-between mb-6 pb-3 border-b-2 border-green-200">
                                <div className="flex items-center gap-2.5">
                                    <div className="bg-gradient-to-br from-green-500 to-teal-600 p-2 rounded-lg shadow-md">
                                        <FaHistory className="text-white text-lg" />
                                    </div>
                                    <h3 className="text-lg font-bold text-gray-800">Analysis History</h3>
                                </div>

                                {/* Filter Controls */}
                                <div className="flex items-center gap-3">
                                    <div className="flex items-center gap-2">
                                        <label className="text-sm font-semibold text-gray-700">Sort by:</label>
                                        <DropDown
                                            name="sortBy"
                                            label="Sort By"
                                            value={sortBy}
                                            onChange={(e) => setSortBy(e.target.value)}
                                            options={[
                                                { value: "date", label: "Date" },
                                                { value: "disease", label: "Disease" },
                                                { value: "status", label: "Status" },
                                            ]}
                                            widthClass="w-32 sm:w-32"
                                            borderClass="border-gray-300"
                                            selectedClass="bg-green-500 text-gray-950"
                                            highlightClass="bg-green-200 text-green-900"
                                            ringClass="ring-green-500"
                                            placeholder="Sort By"
                                            placeholderClass="text-gray-500"
                                        />

                                    </div>

                                    <button
                                        onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
                                        className="px-2.5 py-1 text-xs font-semibold bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all duration-200 flex items-center gap-1 shadow-md"
                                        title={sortOrder === "asc" ? "Ascending Order" : "Descending Order"}
                                    >
                                        {/* Icon wrapper to rotate around center */}
                                        <span className={`inline-block transition-transform duration-200 transform ${sortOrder === "desc" ? "rotate-180" : "rotate-0"}`}>
                                            <FaSortUp className="w-3 h-3" />
                                        </span>
                                        <span>{sortOrder === "asc" ? "Asce" : "Desc"}</span>
                                    </button>

                                </div>
                            </div>

                            {/* 3. UPDATED: Conditional rendering for Loader within the Card */}
                            {predictionsLoading ? (
                                <div className="flex flex-col items-center justify-center py-12">
                                    <AiOutlineLoading3Quarters className="text-4xl text-green-600 animate-spin mb-3" />
                                    <p className="text-green-600 font-medium text-sm animate-pulse">Loading analysis history...</p>
                                </div>
                            ) : predictions && predictions.length > 0 ? (
                                <div className="overflow-x-auto max-h-[800px] overflow-y-auto">
                                    <table className="w-full border-collapse">
                                        <thead className="sticky top-0 z-10">
                                            <tr className="bg-gradient-to-r from-green-50 to-teal-50 border-b-2 border-green-200">
                                                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700">Disease Name</th>
                                                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700">Date</th>
                                                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700">Review Status</th>
                                                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {getSortedPredictions().map((prediction, index) => (
                                                <tr
                                                    key={prediction.id}
                                                    className={`border-b border-gray-200 hover:bg-green-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                                                        }`}
                                                >
                                                    <td className="py-3 px-4 text-sm text-gray-800 font-medium">
                                                        {prediction.result?.predicted_label || 'Skin Analysis'}
                                                    </td>
                                                    <td className="py-3 px-4 text-sm text-gray-600">
                                                        {formatDateTime(prediction.createdAt)}
                                                    </td>
                                                    <td className="py-3 px-4 text-center">
                                                        {reviewRequestsLoading ? (
                                                            <div className="flex items-center justify-center">
                                                                <AiOutlineLoading3Quarters className="text-green-600 h-4 w-4 animate-spin" />
                                                            </div>
                                                        ) : (
                                                            renderStatusBadge(getReviewStatus(prediction.id))
                                                        )}
                                                    </td>
                                                    <td className="py-3 px-4 text-center">
                                                        <div className="flex items-center justify-center gap-2">
                                                            <button
                                                                onClick={() => handleViewAnalysis(prediction.id)}
                                                                className="px-4 py-1.5 bg-green-600 text-white text-xs font-semibold rounded-lg hover:bg-green-700 transition-all duration-200 transform hover:scale-105 shadow-md"
                                                            >
                                                                View
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteAnalysis(prediction.id)}
                                                                className="px-3 py-1.5 bg-red-600 text-white text-xs font-semibold rounded-lg hover:bg-red-700 transition-all duration-200 transform hover:scale-105 shadow-md flex items-center gap-1"
                                                                title="Delete this analysis"
                                                            >
                                                                <FaTrash className="text-xs" />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="text-center py-8">
                                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2">
                                        <FaHistory className="text-gray-400 text-xl" />
                                    </div>
                                    <p className="text-gray-500 font-medium text-sm">No analysis history available</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <ImageCropModal
                isOpen={showCropModal}
                onClose={() => setShowCropModal(false)}
                imageSrc={imageSrc}
                onCropSave={handleCropSave}
            />

            {showUpdatePopup && (
                <UpdateProfilePopup profileData={patient} onClose={() => setShowUpdatePopup(false)}
                    onUpdate={fetchProfile}

                />
            )}
            <PatientReviewModal
                open={showPatientReview}
                onClose={() => {
                    setShowPatientReview(false);
                    setPatientReviewData(null);
                    setReviewError(null);
                }}
                loading={reviewLoading}
                error={reviewError}
                reviewData={patientReviewData}
                currentUser={patient}
            />

            {/* Change Password Modal */}
            <ChangePassword
                isOpen={showPasswordModal}
                onClose={() => setShowPasswordModal(false)}
            />
        </div>
    );
};

export default UserProfile;